import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import { FaShippingFast, FaBox } from "react-icons/fa";
import Select from "react-select";

const ShippingOptions = (
  {
    selectedShippingCountry,
    setSelectedShippingCountry,
    updateAmount,
    amount,
    expressDeliveryDays,
    standardDeliveryDays,
    isCustomizeable,
  },
  context
) => {
  // Fetch the language from the Redux store
  const language = useSelector((state) => state.i18nState.lang);

  const shippingOptions = [
    { value: "germany", label: context.t("Germany") },
    { value: "austria", label: context.t("Austria") },
    { value: "swiss", label: context.t("Switzerland") },
    { value: "italy", label: context.t("Italy") },
    { value: "spain", label: context.t("Spain") },
    { value: "uk", label: context.t("United Kingdom") },
    { value: "norway", label: context.t("Norway") },
    { value: "eu", label: context.t("All other countries in the EU") },
  ];

  const handleShippingChange = (ev) => {
    setSelectedShippingCountry(ev?.value);
  };

  const renderShippingCost = () => {
    switch (selectedShippingCountry) {
      case "germany":
        return context.t("14.90€ + 20% of item value (minimum 10€)");
      case "austria":
        return context.t("8.90€");
      case "swiss":
        return context.t("29.90€ (excl. VAT)");
      case "eu":
      case "italy":
      case "spain":
        return context.t("9.90€");
      case "uk":
      case "norway":
        return context.t("18.90€ (excl. VAT)");
      default:
        return null;
    }
  };

  return (
    <div>
      <h2 className="h4">{context.t("Shipping options")}</h2>
      <Row>
        <Col xs={6}>
          <FormGroup>
            <Label for="amount">{context.t("amount")}</Label>
            <Input
              min="1"
              onChange={updateAmount}
              value={amount}
              placeholder={context.t("amount")}
              style={{ textAlign: "center" }}
              className="mr-1 no-spin-buttons"
              type="number"
              step="1"
            />
          </FormGroup>
        </Col>
        <Col xs={6}>
          <FormGroup>
            <Label for="country">{context.t("shipping country")}</Label>
            <Select
              placeholder={context.t("Germany")}
              options={shippingOptions}
              onChange={handleShippingChange}
              isSearchable
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        {selectedShippingCountry === "germany" && (
          <>
            <Col xs={2} className="text-success text-center">
              <FaShippingFast size="30px" />
            </Col>
            <Col xs={4}>
              <strong>{context.t("Express Shipping")}</strong>
              <br />
              <em>
                {(amount > 50 ? 1 : 0) + expressDeliveryDays - 2} -{" "}
                {(amount > 50 ? 1 : 0) + expressDeliveryDays}{" "}
                {context.t("days")}*
              </em>
            </Col>
          </>
        )}
        <Col xs={2} className="text-primary text-center">
          <FaBox size="30px" />
        </Col>
        <Col xs={4}>
          <strong>{context.t("Standard Shipping")}</strong>
          <br />
          <em>
            {Math.ceil((amount > 50 ? 2 : 0) + standardDeliveryDays / 2)} -{" "}
            {(amount > 50 ? 2 : 0) + standardDeliveryDays} {context.t("days")}*
          </em>
        </Col>
        <Col xs={6} className="pt-3">
          {renderShippingCost()}
        </Col>
        {selectedShippingCountry === "germany" && (
          <Col xs={6} className="pt-3">
            {context.t("5.90€ (Free Shipping from 100€)")}
          </Col>
        )}
      </Row>
      <em className="text-remark">
        {context.t("Not all shipping methods are available for every payment.")}{" "}
        <Link to={`/${language}/p/versandarten/`}>
          {context.t("View more shipping details.")}
        </Link>
      </em>
      {isCustomizeable && (
        <p className="small text-muted mt-3">
          *{" "}
          {context.t(
            "If there are any problems with the delivered printing data or inquiries about your order, please be aware that the delivery time may be delayed. Since our products are usually customized and depending on your design, we cannot provide an exact delivery date in advance. While some production steps are automated and can be performed on any day, certain manual processes may require the standard production days from Monday to Friday. Therefore, our estimated delivery time is based on the regular production schedule. Please note that orders must be placed before 1 pm; otherwise, the production time will be extended by one day."
          )}
        </p>
      )}
    </div>
  );
};

ShippingOptions.propTypes = {
  selectedShippingCountry: PropTypes.string.isRequired,
  setSelectedShippingCountry: PropTypes.func.isRequired,
  updateAmount: PropTypes.func.isRequired,
  amount: PropTypes.number.isRequired,
  expressDeliveryDays: PropTypes.number.isRequired,
  standardDeliveryDays: PropTypes.number.isRequired,
  isCustomizeable: PropTypes.bool,
};

/**  define proptype for the 'translation' function  */
ShippingOptions.contextTypes = {
  t: PropTypes.func,
};

export default ShippingOptions;
